import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import * as menuItemsApi from '../endpoints/menu-items';
import { ListMenuItemsParams, PaginatedMenuItemResponse, SearchMenuItemsParams } from '../endpoints/menu-items';
import { MenuItem } from '@/types/menu-item';

/**
 * Query keys for menu items
 */
export const MENU_ITEMS_QUERY_KEYS = {
  all: ['menu-items'] as const,
  lists: () => [...MENU_ITEMS_QUERY_KEYS.all, 'list'] as const,
  list: (params: ListMenuItemsParams) => [...MENU_ITEMS_QUERY_KEYS.lists(), params] as const,
  searches: () => [...MENU_ITEMS_QUERY_KEYS.all, 'search'] as const,
  search: (params: SearchMenuItemsParams) => [...MENU_ITEMS_QUERY_KEYS.searches(), params] as const,
  details: () => [...MENU_ITEMS_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...MENU_ITEMS_QUERY_KEYS.details(), id] as const,
} as const;

/**
 * Domain-oriented query options for menu item operations
 * Provides reusable queryOptions that can be used in hooks or prefetching
 */

/**
 * Query options for listing menu items with pagination and filtering
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Query options for the menu items list
 *
 * @example
 * ```typescript
 * // List all menu items
 * const options = menuItemsListQueryOptions();
 *
 * // Filter by business
 * const options = menuItemsListQueryOptions({ business_id: 'business-123' });
 *
 * // With pagination
 * const options = menuItemsListQueryOptions({ page: 2, per_page: 10 });
 * ```
 */
export const menuItemsListQueryOptions = (params: ListMenuItemsParams = {}) =>
  queryOptions({
    queryKey: MENU_ITEMS_QUERY_KEYS.list(params),
    queryFn: async (): Promise<PaginatedMenuItemResponse> => {
      return menuItemsApi.listMenuItems(params);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for searching menu items with text query
 *
 * @param params - Search parameters including query text, business filter, and pagination
 * @returns Query options for the menu items search
 *
 * @example
 * ```typescript
 * // Search for "pizza" across all businesses
 * const options = menuItemsSearchQueryOptions({
 *   query: 'pizza',
 *   per_page: 10
 * });
 *
 * // Search within a specific business
 * const options = menuItemsSearchQueryOptions({
 *   query: 'burger',
 *   business_id: 'business-123'
 * });
 * ```
 */
export const menuItemsSearchQueryOptions = (params: SearchMenuItemsParams) =>
  queryOptions({
    queryKey: MENU_ITEMS_QUERY_KEYS.search(params),
    queryFn: async (): Promise<PaginatedMenuItemResponse> => {
      return menuItemsApi.searchMenuItems(params);
    },
    enabled: (params.query?.trim().length ?? 0) >= 1, // Only search if query is at least 1 character
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for fetching a single menu item by ID
 *
 * @param id - Menu item ID
 * @returns Query options for the menu item detail
 *
 * @example
 * ```typescript
 * const options = menuItemDetailQueryOptions('menu-item-123');
 * ```
 */
export const menuItemDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: MENU_ITEMS_QUERY_KEYS.detail(id),
    queryFn: async (): Promise<MenuItem> => {
      return menuItemsApi.getMenuItemById(id);
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

export const menuItemsSearchInfiniteQueryOptions = (params: Omit<SearchMenuItemsParams, 'page'>) =>
  infiniteQueryOptions({
    queryKey: [...MENU_ITEMS_QUERY_KEYS.searches(), params, 'infinite'] as const,
    queryFn: async ({ pageParam }): Promise<PaginatedMenuItemResponse> => {
      return menuItemsApi.searchMenuItems({ ...params, page: pageParam as number });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.total_pages ? lastPage.page + 1 : undefined,
    enabled: (params.query?.trim().length ?? 0) >= 1,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 15,
    meta: { persist: false },
  });

export const menuItemsInfiniteQueryOptions = (businessId: string) =>
  infiniteQueryOptions({
    queryKey: [...MENU_ITEMS_QUERY_KEYS.lists(), businessId, 'infinite'],
    queryFn: async ({ pageParam }): Promise<PaginatedMenuItemResponse> => {
      return menuItemsApi.listMenuItems({
        business_id: businessId,
        page: pageParam,
        per_page: 20,
        sort_by: 'name',
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.total_pages ? lastPage.page + 1 : undefined,
    enabled: !!businessId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 15,
    meta: { persist: false },
  });
